import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import TitleIcon from '@mui/icons-material/Title'
import AnalyticsIcon from '@mui/icons-material/Analytics'

import { MultiTableConfigTable, ReportPillarWidgetData } from '../../../types/gotypes'
import { GenAIWizard, GenAIWizardCompleteResult } from './genai/GenAIWizard'
import {
  appendPillarAnalysisEntry,
  pillarEntryLabel,
  removePillarEntry,
  setPillarHeading,
} from './ReportPillarWidget'

/**
 * The Report Pillar editor (#1080).
 *
 * THERE IS NO WIDGET-TYPE SWITCHER HERE, and that is the point. A pillar used
 * to be a nested `layout` cell edited through the generic Edit Cell modal,
 * where picking a different widget type replaced the whole column with one
 * empty widget — #1075, confirmed data loss. A guard was bolted on; this
 * removes the surface instead, so the destruction class is unrepresentable
 * rather than defended against.
 *
 * SCOPE (Adam, 2026-08-24, resolving #1080 Q2): edit the heading, add a Gen AI
 * card, remove an entry. Deleting the whole pillar stays on the cell chrome,
 * with its own "contains N cards" confirmation.
 *
 * REORDER IS DELIBERATELY WITHHELD in this build. Entry order IS the report's
 * narrative and nothing else records it, so who may re-cut that narrative is
 * Matt's call, not a default — it ships when #1080 Q2 is answered. Adding the
 * control later is additive: the data model is already an ordered list.
 */

export interface ReportPillarEditorProps {
  data: ReportPillarWidgetData
  customer: string
  job: string
  dashboardId: string
  /** #1810: the page's filter map, for the add-card wizard's preview run. */
  jobFilters?: Record<string, string>
  onChange: (data: ReportPillarWidgetData) => void
  /** Tables the newly added card needs created — handed to the save path the
   *  same way the Gen AI card editor hands over its own. */
  onPendingTablesChange: (entryIndex: number, tables: MultiTableConfigTable[]) => void
}

const ReportPillarEditor: React.FC<ReportPillarEditorProps> = ({
  data,
  customer,
  job,
  dashboardId,
  jobFilters,
  onChange,
  onPendingTablesChange,
}) => {
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null)
  const [addingCard, setAddingCard] = useState(false)

  const handleHeadingChange = (heading: string) => {
    onChange(setPillarHeading(data, heading))
  }

  const handleConfirmRemove = () => {
    if (confirmRemove === null) return
    onChange(removePillarEntry(data, confirmRemove))
    setConfirmRemove(null)
  }

  const handleWizardComplete = (result: GenAIWizardCompleteResult) => {
    const next = appendPillarAnalysisEntry(data, {
      widget_type: 'multi_table_analysis',
      multi_table_analysis_data: {
        multi_table_id: result.multiTableId || '',
        job,
        title: result.title || undefined,
        persona: 'Executive Summarizer',
        analysis_instructions: result.instructions || undefined,
      },
    })
    onChange(next)
    onPendingTablesChange(next.entries.length - 1, result.tables)
    setAddingCard(false)
  }

  if (addingCard) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Add a Gen AI card to “{data.heading || 'this pillar'}”
        </Typography>
        <GenAIWizard
          customer={customer}
          job={job}
          dashboardId={dashboardId}
          jobFilters={jobFilters}
          onComplete={handleWizardComplete}
          onCancel={() => setAddingCard(false)}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Pillar heading"
        value={data.heading || ''}
        onChange={(e) => handleHeadingChange(e.target.value)}
        fullWidth
        size="small"
        helperText="Shown on the pillar's title band."
      />

      {data.manifest && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Compiled from the <strong>{data.manifest}</strong> manifest
          {data.manifest_pillar_id ? <> · pillar <code>{data.manifest_pillar_id}</code></> : null}.
          {' '}Card order is the report's narrative and is not editable here.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          Cards in this pillar
        </Typography>
        <List dense disablePadding>
          {data.entries.map((entry, index) => (
            <ListItem
              key={index}
              divider
              secondaryAction={
                <Tooltip title="Remove from this pillar">
                  <IconButton edge="end" size="small" onClick={() => setConfirmRemove(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5, color: 'text.secondary' }}>
                {entry.kind === 'title_hero' ? <TitleIcon fontSize="small" /> : <AnalyticsIcon fontSize="small" />}
              </Box>
              <ListItemText
                primary={pillarEntryLabel(data, index)}
                secondary={entry.kind === 'title_hero' ? 'Pillar heading' : 'Gen AI Insights'}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              {entry.kind === 'analysis' && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={entry.manifest_section_id || 'added by hand'}
                  color={entry.manifest_section_id ? 'default' : 'info'}
                  sx={{ ml: 1, fontFamily: entry.manifest_section_id ? 'monospace' : undefined }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      <Box>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          color="info"
          size="small"
          onClick={() => setAddingCard(true)}
          disabled={!job}
        >
          Add Gen AI card
        </Button>
        {!job && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Open a job to add a card.
          </Typography>
        )}
      </Box>

      <Dialog open={confirmRemove !== null} onClose={() => setConfirmRemove(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Remove from this pillar?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            “{confirmRemove !== null ? pillarEntryLabel(data, confirmRemove) : ''}” will be removed from
            this pillar. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} color="inherit" size="small">Cancel</Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained" size="small">Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReportPillarEditor
